import { useState, useEffect, useRef } from "react";
import {
  X,
  Building,
  MapPin,
  Package,
  User,
  Calendar,
  Check,
  Edit,
  Save,
  Camera,
  Clock,
} from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import {
  Encomienda,
  obtenerEncomiendas,
  modificarEncomienda,
  modificarFotoEncomienda,
  cambiarAEntregado,
} from "../../../../api/encomiendaService";
import { obtenerTorres, Torre } from "../../../../api/torreService";
import { obtenerApartamentos, Apartamento } from "../../../../api/apartamentoService";
import { obtenerTiposEncomienda, TipoEncomienda } from "../../../../api/tipoEncomiendaService";

interface DetailEncomiendaModalProps {
  isOpen: boolean;
  encomienda: Encomienda;
  onClose: (shouldReload: boolean) => void;
}

export default function DetailEncomiendaModal({
  isOpen,
  encomienda,
  onClose,
}: DetailEncomiendaModalProps) {
  const { session } = useAuth();
  
  const lastLoadedEncomiendaRef = useRef<string>("");
  const isLoadingRef = useRef(false);
  
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChangingPhoto, setIsChangingPhoto] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [encomiendaData, setEncomiendaData] = useState<Encomienda>(encomienda);
  const [selectedTorre, setSelectedTorre] = useState(encomienda.tor_codigo);
  const [selectedApartamento, setSelectedApartamento] = useState(encomienda.apt_codigo);
  const [selectedTipo, setSelectedTipo] = useState(encomienda.ten_codigo);
  const [nombreMensajero, setNombreMensajero] = useState(encomienda.enc_nombre_mensajero);
  const [previewImage, setPreviewImage] = useState(encomienda.enc_ruta_foto || encomienda.enc_foto_base64);
  const [torres, setTorres] = useState<Torre[]>([]);
  const [apartamentos, setApartamentos] = useState<Apartamento[]>([]);
  const [tiposEncomienda, setTiposEncomienda] = useState<TipoEncomienda[]>([]);

  const isEntregado = (encomiendaData.een_nombre || "").toLowerCase().includes("entregad");

  const loadEncomiendaDetails = async () => {
    try {
      setIsLoadingDetails(true);

      const response = await obtenerEncomiendas({
        unidad_residencial_codigo: "",
        torre_codigo: "",
        apartamento_codigo: "",
        estatus_codigo: "",
        encomienda_codigo: String(encomienda.enc_codigo || ""),
      });

      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        const apiData = response.object_[0] as any;
        
        const fechaRecibido = apiData.enc_fecha_recibido || "";
        const fechaRecibidoParts = fechaRecibido.split(" ");
        const fechaRecibidoDate = fechaRecibidoParts[0] || fechaRecibido;
        const horaRecibido = fechaRecibidoParts[1] || apiData.enc_hora_recibido || "";
        
        const fechaEntregado = apiData.enc_fecha_entregado || "";
        const fechaEntregadoParts = fechaEntregado.split(" ");
        const fechaEntregadoDate = fechaEntregadoParts[0] || fechaEntregado;
        const horaEntregado = fechaEntregadoParts[1] || apiData.enc_hora_entregado || "";
        
        const encomiendaDetalle: Encomienda = {
          enc_codigo: String(apiData.enc_codigo || ""),
          enc_ruta_foto: apiData.enc_ruta_foto,
          enc_foto_base64: apiData.enc_foto_base64,
          apt_codigo: apiData.enc_apartamento || "",
          apt_nombre: apiData.enc_apartamento_nombre || "",
          tor_codigo: apiData.enc_torre || "",
          tor_nombre: apiData.enc_torre_nombre || "",
          ten_codigo: apiData.enc_tipo || "",
          ten_nombre: apiData.enc_tipo_nombre || "",
          enc_nombre_mensajero: apiData.enc_nombre_mensajero || "",
          enc_fecha_recibido: fechaRecibidoDate,
          enc_hora_recibido: horaRecibido,
          een_codigo: apiData.enc_estatus || "",
          een_nombre: apiData.enc_estatus_nombre || "",
          enc_fecha_entregado: fechaEntregadoDate || undefined,
          enc_hora_entregado: horaEntregado || undefined,
          usu_recibio_nombre: apiData.enc_recibido_por_nombre || "",
        };
        
        setEncomiendaData(encomiendaDetalle);
        setSelectedTorre(encomiendaDetalle.tor_codigo);
        setSelectedApartamento(encomiendaDetalle.apt_codigo);
        setSelectedTipo(encomiendaDetalle.ten_codigo);
        setNombreMensajero(encomiendaDetalle.enc_nombre_mensajero);
        setPreviewImage(encomiendaDetalle.enc_ruta_foto || encomiendaDetalle.enc_foto_base64);
        setImageError(false);
      }
    } catch (error) {
      console.error("Error al cargar detalles de encomienda:", error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const encomiendaId = String(encomienda.enc_codigo || "");
      const isNewEncomienda = lastLoadedEncomiendaRef.current !== encomiendaId;
      
      if (isNewEncomienda && !isLoadingRef.current) {
        lastLoadedEncomiendaRef.current = encomiendaId;
        loadEncomiendaDetails().then(() => {
          loadCatalogos();
        });
      }
    } else {
      setTorres([]);
      setApartamentos([]);
      setTiposEncomienda([]);
      lastLoadedEncomiendaRef.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, encomienda.enc_codigo]);

  const loadCatalogos = async () => {
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) return;

      const torresResponse = await obtenerTorres({
        "unidad residencial codigo": unidadCodigo,
      });
      
      if (torresResponse.code === "1" && torresResponse.object_) {
        setTorres(torresResponse.object_);
        
        const torreActual = encomiendaData.tor_codigo;
        if (torreActual && torresResponse.object_.some(t => t.tor_codigo === torreActual)) {
          await loadApartamentos(torreActual);
        }
      }

      const tiposResponse = await obtenerTiposEncomienda();
      if (tiposResponse.code === "1" && tiposResponse.object_) {
        setTiposEncomienda(tiposResponse.object_);
      }
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los datos necesarios",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      isLoadingRef.current = false;
    }
  };

  const loadApartamentos = async (torreCodigo: string) => {
    if (!torreCodigo || torreCodigo.trim() === "") {
      setApartamentos([]);
      return;
    }

    try {
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) {
        setApartamentos([]);
        return;
      }

      const response = await obtenerApartamentos({
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": torreCodigo,
      });

      if (response.code === "1" && response.object_) {
        setApartamentos(response.object_);
      } else {
        setApartamentos([]);
      }
    } catch (error) {
      console.error("Error al cargar apartamentos:", error);
      setApartamentos([]);
    }
  };

  const handleTorreChange = (value: string) => {
    setSelectedTorre(value);
    setSelectedApartamento("");
    setApartamentos([]);
    if (value) {
      loadApartamentos(value);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: "warning",
          title: "Archivo muy grande",
          text: "La imagen no debe superar los 5MB",
          confirmButtonColor: "#3d5a40",
          didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
        });
        return;
      }

      if (!file.type.startsWith("image/")) {
        Swal.fire({
          icon: "warning",
          title: "Formato inválido",
          text: "Por favor seleccione una imagen válida",
          confirmButtonColor: "#3d5a40",
          didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;

        try {
          setIsChangingPhoto(true);

          const payload = {
            codigo: String(encomiendaData.enc_codigo || ""),
            foto_base64: base64String,
          };

          const response = await modificarFotoEncomienda(payload);

          if (response.code === "1" || response.code === 1) {
            setPreviewImage(base64String);
            await loadEncomiendaDetails();
            Swal.fire({
              icon: "success",
              title: "¡Foto actualizada!",
              text: response.message || "La foto se ha actualizado exitosamente",
              confirmButtonColor: "#3d5a40",
              timer: 2000,
              timerProgressBar: true,
              didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
            });
          } else {
            throw new Error(response.message || "Error al actualizar la foto");
          }
        } catch (error) {
          console.error("Error al cambiar foto:", error);
          const errorMessage = error instanceof Error ? error.message : "Error al actualizar la foto";
          const mensajeTraducido = errorMessage.includes("Request failed") 
            ? "Error al actualizar la foto. Por favor, intente nuevamente."
            : errorMessage.includes("Network Error")
            ? "Error de conexión. Verifique su conexión a internet."
            : errorMessage;
          
          Swal.fire({
            icon: "error",
            title: "Error",
            text: mensajeTraducido,
            confirmButtonColor: "#d33",
            didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
          });
        } finally {
          setIsChangingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMarcarEntregado = async () => {
    const result = await Swal.fire({
      title: "¿Marcar como entregado?",
      text: "¿Está seguro que desea marcar esta encomienda como entregada?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3d5a40",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, marcar como entregado",
      cancelButtonText: "Cancelar",
      didOpen: (popup) => {
        (popup as HTMLElement).style.zIndex = '10001';
      },
    });

    if (!result.isConfirmed) return;

    try {
      setIsSubmitting(true);

      const response = await cambiarAEntregado({
        codigo: String(encomiendaData.enc_codigo || ""),
      });

      if (response.code === "1" || response.code === 1) {
        Swal.fire({
          icon: "success",
          title: "¡Encomienda entregada!",
          text: response.message || "La encomienda se ha marcado como entregada",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
        });
        onClose(true);
      } else {
        throw new Error(response.message || "Error al marcar como entregado");
      }
    } catch (error) {
      console.error("Error al marcar como entregado:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al marcar como entregado";
      const mensajeTraducido = errorMessage.includes("Request failed") 
        ? "Error al marcar como entregado. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedApartamento) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione un apartamento",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!selectedTipo) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione el tipo de encomienda",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        codigo: String(encomiendaData.enc_codigo || ""),
        nombre_mensajero: nombreMensajero.trim(),
        apartamento_codigo: selectedApartamento,
        tipo_codigo: selectedTipo,
      };

      const response = await modificarEncomienda(payload);

      if (response.code === "1" || response.code === 1) {
        await loadEncomiendaDetails();
        Swal.fire({
          icon: "success",
          title: "¡Cambios guardados!",
          text: response.message || "La encomienda se ha actualizado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
        });
        setIsEditing(false);
        onClose(true);
      } else {
        throw new Error(response.message || "Error al actualizar la encomienda");
      }
    } catch (error) {
      console.error("Error al guardar cambios:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al actualizar la encomienda";
      const mensajeTraducido = errorMessage.includes("Request failed") 
        ? "Error al actualizar la encomienda. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const getStatusColor = (status: string) => {
    const statusLower = (status || "").toLowerCase();
    if (statusLower.includes("entregad")) return "text-green-600 bg-green-50 border-green-200";
    if (statusLower.includes("portería") || statusLower.includes("porteria"))
      return "text-orange-600 bg-orange-50 border-orange-200";
    return "text-gray-600 bg-gray-50 border-gray-200";
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-military-600 flex items-center gap-2">
              <Package size={28} />
              Detalle de Encomienda
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Código: {encomiendaData.enc_codigo}
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={isSubmitting || isLoadingDetails}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {isLoadingDetails ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
              <p className="text-gray-600">Cargando detalles...</p>
            </div>
          ) : (
            <>
          <div className="flex justify-center">
            <span
              className={`px-6 py-2 rounded-full text-sm font-semibold border ${getStatusColor(
                encomiendaData.een_nombre || ""
              )}`}
            >
              {encomiendaData.een_nombre || "N/A"}
            </span>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Foto de la Encomienda
            </label>
            <div className="relative bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: "400px", maxHeight: "500px" }}>
              {previewImage && !imageError ? (
                <img
                  src={previewImage}
                  alt="Encomienda"
                  className="max-w-full max-h-full object-contain"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ minHeight: "400px" }}>
                  <Package size={64} className="text-gray-400" />
                </div>
              )}
              
              {!isEntregado && (
                <div className="absolute bottom-4 right-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="photo-upload"
                    disabled={isChangingPhoto}
                  />
                  <label
                    htmlFor="photo-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 bg-military-500 text-white rounded-lg cursor-pointer hover:bg-military-600 transition shadow-lg ${
                      isChangingPhoto ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {isChangingPhoto ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Cambiando...
                      </>
                    ) : (
                      <>
                        <Camera size={20} />
                        Cambiar foto
                      </>
                    )}
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building size={16} className="inline mr-1" />
                Torre
              </label>
              {isEditing ? (
                <select
                  value={selectedTorre}
                  onChange={(e) => handleTorreChange(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                >
                  {torres.map((torre) => (
                    <option key={torre.tor_codigo} value={torre.tor_codigo}>
                      {torre.tor_nombre}
                    </option>
                  ))}
                </select>
              ) : (
                <Input value={encomiendaData.tor_nombre || ""} disabled className="bg-gray-50" />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin size={16} className="inline mr-1" />
                Apartamento
              </label>
              {isEditing ? (
                <select
                  value={selectedApartamento}
                  onChange={(e) => setSelectedApartamento(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                >
                  {apartamentos.map((apt) => (
                    <option key={apt.apt_codigo} value={apt.apt_codigo}>
                      {apt.apt_nombre}
                    </option>
                  ))}
                </select>
              ) : (
                <Input value={encomiendaData.apt_nombre || ""} disabled className="bg-gray-50" />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Package size={16} className="inline mr-1" />
                Tipo de Encomienda
              </label>
              {isEditing ? (
                <select
                  value={selectedTipo}
                  onChange={(e) => setSelectedTipo(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                >
                  {tiposEncomienda.map((tipo) => (
                    <option key={tipo.ten_codigo} value={tipo.ten_codigo}>
                      {tipo.ten_nombre}
                    </option>
                  ))}
                </select>
              ) : (
                <Input value={encomiendaData.ten_nombre || ""} disabled className="bg-gray-50" />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User size={16} className="inline mr-1" />
                Nombre del Mensajero
              </label>
              {isEditing ? (
                <Input
                  type="text"
                  value={nombreMensajero}
                  onChange={(e) => setNombreMensajero(e.target.value)}
                  disabled={isSubmitting}
                  placeholder="Nombre del mensajero"
                />
              ) : (
                <Input
                  value={encomiendaData.enc_nombre_mensajero || "N/A"}
                  disabled
                  className="bg-gray-50"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} className="inline mr-1" />
                Fecha Recibido
              </label>
              <Input
                value={encomiendaData.enc_fecha_recibido || ""}
                disabled
                className="bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock size={16} className="inline mr-1" />
                Hora Recibido
              </label>
              <Input
                value={encomiendaData.enc_hora_recibido || ""}
                disabled
                className="bg-gray-50"
              />
            </div>

            {isEntregado && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar size={16} className="inline mr-1" />
                    Fecha Entregado
                  </label>
                  <Input
                    value={encomiendaData.enc_fecha_entregado || "N/A"}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock size={16} className="inline mr-1" />
                    Hora Entregado
                  </label>
                  <Input
                    value={encomiendaData.enc_hora_entregado || "N/A"}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                {encomiendaData.usu_recibio_nombre && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User size={16} className="inline mr-1" />
                      Recibido Por
                    </label>
                    <Input
                      value={encomiendaData.usu_recibio_nombre}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>
                )}
              </>
            )}
          </div>
          </>
          )}
          {!isEntregado && !isLoadingDetails && (
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
              {isEditing ? (
                <>
                  <Button
                    onClick={() => setIsEditing(false)}
                    variant="outline"
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    disabled={isSubmitting}
                    className="flex-1 bg-military-500 hover:bg-military-600 text-white"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save size={20} className="mr-2" />
                        Guardar Cambios
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => setIsEditing(true)}
                    variant="outline"
                    className="flex-1 border-military-500 text-military-600 hover:bg-military-50"
                  >
                    <Edit size={20} className="mr-2" />
                    Modificar
                  </Button>
                  <Button
                    onClick={handleMarcarEntregado}
                    disabled={isSubmitting}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <Check size={20} className="mr-2" />
                        Marcar como Entregado
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

